import React, { useContext } from "react";
import { AppContext } from "../../contexts/AppContextProvider";
import { NavLink } from "react-router-dom";
import { connect } from "react-redux";

import { logoutRequest } from "./../../actions";
import NavTenantsProjects from "./NavTenantsProjects.js";
import {
  shouldDisplayProject,
  PROJECTS_WITHOUT_BOARDS_WHITELIST,
} from "../../config/projectWhitelist";

/**
 * Renders the mobile version of Navbar
 * @param {*} props
 */
const MobileNav = (props) => {
  const burgerRef = React.createRef();
  const context = useContext(AppContext);

  const handleLogout = () => {
    const { dispatch } = props;
    dispatch(logoutRequest());
  };

  const handleClick = () => {
    burgerRef.current.click();
  };

  const handleProjectClick = (project) => {
    if (project && project.id) {
      localStorage.setItem("defaultProjectID", project.id);
      context.dispatch({
        type: "APP-SET-PROJECT_ID",
        projectID: project.id,
      });
    }
    handleClick();
  };

  const renderProjects = () => {
    const projects = context.state.currentProject.data.projects;

    let sorted_projects = projects.sort((a, b) => {
      if (a.title < b.title) {
        return -1;
      }
      if (a.title > b.title) {
        return 1;
      }
      return 0;
    });

    // Filter projects that should be displayed
    const currentVersion = localStorage.configVersion || "production";
    sorted_projects = projects
      .filter((p) => {
        // For whitelisted projects in production, include them
        const projectId = parseInt(p.id, 10);
        const isWhitelisted =
          PROJECTS_WITHOUT_BOARDS_WHITELIST.includes(projectId);
        const isProduction = currentVersion !== "development";

        if (isWhitelisted && isProduction) {
          return true; // Include whitelisted projects in production
        }

        // For other projects, just check if they should be displayed
        return shouldDisplayProject(p, currentVersion);
      })
      .sort((a, b) => a.position - b.position);

    return sorted_projects.map((project, key) => (
      <div key={project.title} className="nav-dropdown__item">
        <NavLink
          key={key}
          className="nav-mobile__item"
          to={`/${project.slug}`}
          activeClassName="nav-mobile__item--is-on"
          onClick={() => handleProjectClick(project)}
        >
          {project.title}
        </NavLink>
        <img
          src={(() => {
            let logo;
            try {
              logo = require(`./../../i/${project.logo}`);
            } finally {
              return logo;
            }
          })()}
          alt={project.title}
          className="nav-dropdown__icon"
        />
      </div>
    ));
  };

  const renderQuery = () => {
    const projects = context.state.currentProject.data.projects;

    let sorted_projects = projects.sort((a, b) => {
      if (a.title < b.title) {
        return -1;
      }
      if (a.title > b.title) {
        return 1;
      }
      return 0;
    });

    // Filter projects that don't have boards and get the correct version
    sorted_projects = projects
      .filter((p) => p.boards.length > 0)
      .filter((p) => p.version === localStorage.configVersion || "production")
      .filter((p) => p.nav?.props?.queryBuilder === true)
      .sort((a, b) => a.position - b.position);

    return sorted_projects.map((project, key) => (
      <div key={project.title} className="nav-dropdown__item">
        <NavLink
          key={key}
          className="nav-mobile__item"
          to={`/${project.slug}/query`}
          activeClassName="nav-mobile__item--is-on"
          onClick={() => handleProjectClick(project)}
        >
          {project.title}
        </NavLink>
        <img
          src={(() => {
            let logo;
            try {
              logo = require(`./../../i/${project.logo}`);
            } finally {
              return logo;
            }
          })()}
          alt={project.title}
          className="nav-dropdown__icon"
        />
      </div>
    ));
  };

  const renderBoards = (showArchivedOnly = false) => {
    const { project, data } = context.state.currentProject;
    if (!project) return null;

    const projectData = data.projects.find((p) => p.slug === project);
    if (!projectData || !projectData.boards || projectData.boards.length === 0)
      return null;

    const boards = projectData.boards;
    const boards_sorted = boards.sort((a, b) => a.position - b.position);

    const filter = !showArchivedOnly
      ? (page) => page.slug !== "insights" && !page.archived
      : (page) => page.slug !== "insights" && page.archived;

    const filteredOptions = boards_sorted.filter(filter);
    return (
      <>
        {(!showArchivedOnly ||
          (showArchivedOnly && filteredOptions.length > 0)) && (
          <h3
            className={"nav-mobile__title" + (showArchivedOnly ? " mt-3" : "")}
          >
            {!showArchivedOnly ? "Boards" : "Archived"}
          </h3>
        )}
        {boards_sorted.filter(filter).map((board, key) => (
          <NavLink
            key={key}
            className="nav-mobile__item"
            to={`/${project}/${board.slug}`}
            activeClassName="nav-mobile__item--is-on"
            onClick={handleClick}
          >
            {board.title}
          </NavLink>
        ))}
      </>
    );
  };

  const getUserName = () => {
    const userName = localStorage.user;
    return `${userName}`;
  };

  const showSources = () => {
    if (localStorage.roles && localStorage.roles.includes("admin")) {
      return (
        <NavLink
          className="p-0 nav-mobile__item"
          to="/settings/sources"
          onClick={handleClick}
        >
          Sources
        </NavLink>
      );
    }
  };

  const showInsights = () => {
    if (insights_url && show_project_insights) {
      return (
        <NavLink
          className="p-0 nav-mobile__item"
          to={`/${project.slug}/insights`}
          onClick={handleClick}
        >
          Insights
        </NavLink>
      );
    } else {
      return null;
    }
  };

  const showUsers = () => {
    if (localStorage.roles && localStorage.roles.includes("admin")) {
      return (
        <NavLink
          className="p-0 nav-mobile__item"
          to="/settings/users"
          onClick={handleClick}
        >
          Users
        </NavLink>
      );
    } else {
      return null; // Return null if the condition is not met
    }
  };

  const { insights_url, show_project_insights } = props;
  const { data, project } = useContext(AppContext).state.currentProject;

  const has_multiple_tenants = data.tenants.length > 1;

  // Check if current project has boards
  const currentProject = data.projects.find((p) => p.slug === project);
  const hasBoards =
    currentProject && currentProject.boards && currentProject.boards.length > 0;

  const boardsContent = data && hasBoards ? renderBoards() : null;
  const archivedBoardsContent = data && hasBoards ? renderBoards(true) : null;

  return (
    <div className="col-auto ml-auto col-lg-9 d-md-none">
      <div className="burger">
        <input
          type="checkbox"
          ref={burgerRef}
          onChange={(e) => {
            e.target.checked
              ? (document.body.style.overflow = "hidden")
              : (document.body.style.overflow = null);
          }}
        />

        <span className="burger-line" />
        <span className="burger-line" />
        <span className="burger-line" />

        <div className="menu">
          <div className="menu__section">
            <h3 className="nav-mobile__title">Properties</h3>
            {renderProjects()}
          </div>
          <div className="menu__section">
            <h3 className="nav-mobile__title">Query</h3>
            {renderQuery()}
          </div>

          {boardsContent && (
            <div className="menu__section">{boardsContent}</div>
          )}
          {archivedBoardsContent && (
            <div className="menu__section">{archivedBoardsContent}</div>
          )}

          {has_multiple_tenants && (
            <div className="menu__section">
              <h3 className="nav-mobile__title">Tenants</h3>
              <NavTenantsProjects
                tenants={data.tenants}
                className={"nav-mobile__item"}
              />
            </div>
          )}
          <div className="menu__section">
            <h3 className="nav-mobile__title text-capitalize">
              {getUserName()}
            </h3>

            {showInsights()}
            {showSources()}
            {showUsers()}
            <button
              className="p-0 btn btn-link nav-mobile__item"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default connect()(MobileNav);
